/**
 * StationStatus durable cache — IndexedDB implementation + in-memory fallback.
 *
 * Spec §7 (web): one object store keyed by naptanId, a meta record for the ETag.
 * `replace` is wholesale: clears all entries and rewrites atomically in a single
 * transaction, then notifies subscribers. Absent key = no disruption (Open).
 *
 * Falls back to `InMemoryStationStatusCache` when IndexedDB is unavailable
 * (e.g. SSR / test environments that don't supply fake-indexeddb).
 */
import type { StationStatus, StationStatusSnapshot } from './types'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/** Callback signature for cache snapshot subscribers. */
export type StationStatusCacheSubscriber = (snapshot: StationStatusSnapshot) => void

/**
 * Domain-agnostic status cache interface (spec §0 cache API contract).
 * Mirrors iOS `_StationStatusCache` and Android `StationStatusCache`.
 */
export interface StationStatusCache {
  /** Current status for a single station; `undefined` = Open. */
  status(naptanId: string): Promise<StationStatus | undefined>
  /** Full snapshot of all known statuses. */
  snapshot(): Promise<StationStatusSnapshot>
  /** The ETag from the last successful server response; `undefined` on cold start. */
  etag(): Promise<string | undefined>
  /**
   * Replace the entire cache snapshot atomically. Absent keys from the previous
   * snapshot are cleared (no merging). Notifies all subscribers after the write.
   */
  replace(snapshot: StationStatusSnapshot, etag: string): Promise<void>
  /**
   * Subscribe to snapshot updates. Called immediately with the current snapshot
   * on subscription (warm-launch paint), then on every `replace()`.
   * Returns an unsubscribe function.
   */
  updates(subscriber: StationStatusCacheSubscriber): () => void
}

// ---------------------------------------------------------------------------
// IndexedDB constants
// ---------------------------------------------------------------------------

const DB_NAME = 'metro-station-status'
const DB_VERSION = 1
const STORE_STATUSES = 'statuses'
const STORE_META = 'meta'
const META_ETAG_KEY = '__etag__'

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

function openDb(name: string = DB_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_STATUSES)) {
        db.createObjectStore(STORE_STATUSES) // keyed by naptanId
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META) // single 'meta' key for ETag
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function getAllKeysAndValues(store: IDBObjectStore): Promise<StationStatusSnapshot> {
  return new Promise((resolve, reject) => {
    const result: Record<string, StationStatus> = {}
    const keysReq = store.getAllKeys()
    const valuesReq = store.getAll()
    let keysResult: IDBValidKey[] | null = null
    let valuesResult: StationStatus[] | null = null

    const tryResolve = () => {
      if (keysResult !== null && valuesResult !== null) {
        for (let i = 0; i < keysResult.length; i++) {
          result[String(keysResult[i])] = valuesResult[i]
        }
        resolve(result)
      }
    }

    keysReq.onsuccess = () => {
      keysResult = keysReq.result
      tryResolve()
    }
    valuesReq.onsuccess = () => {
      valuesResult = valuesReq.result as StationStatus[]
      tryResolve()
    }
    keysReq.onerror = () => reject(keysReq.error)
    valuesReq.onerror = () => reject(valuesReq.error)
  })
}

// ---------------------------------------------------------------------------
// IndexedDbStationStatusCache
// ---------------------------------------------------------------------------

/**
 * Durable IndexedDB-backed cache (spec §7). Two object stores:
 * - `statuses`: naptanId → StationStatus
 * - `meta`: string key → value (ETag stored at `__etag__`)
 *
 * `replace` runs inside a single read-write transaction spanning both stores
 * so there is never a state where statuses are updated but the ETag is stale
 * or vice versa.
 *
 * Subscribers are notified synchronously after the transaction commits.
 *
 * @param dbName - Override the default DB name (useful for test isolation).
 */
export class IndexedDbStationStatusCache implements StationStatusCache {
  private readonly dbPromise: Promise<IDBDatabase>
  private readonly subscribers = new Set<StationStatusCacheSubscriber>()

  constructor(dbName: string = DB_NAME) {
    this.dbPromise = openDb(dbName)
  }

  async status(naptanId: string): Promise<StationStatus | undefined> {
    const db = await this.dbPromise
    const tx = db.transaction(STORE_STATUSES, 'readonly')
    return idbRequest<StationStatus | undefined>(tx.objectStore(STORE_STATUSES).get(naptanId))
  }

  async snapshot(): Promise<StationStatusSnapshot> {
    const db = await this.dbPromise
    const tx = db.transaction(STORE_STATUSES, 'readonly')
    return getAllKeysAndValues(tx.objectStore(STORE_STATUSES))
  }

  async etag(): Promise<string | undefined> {
    const db = await this.dbPromise
    const tx = db.transaction(STORE_META, 'readonly')
    return idbRequest<string | undefined>(tx.objectStore(STORE_META).get(META_ETAG_KEY))
  }

  async replace(snapshot: StationStatusSnapshot, etag: string): Promise<void> {
    const db = await this.dbPromise
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([STORE_STATUSES, STORE_META], 'readwrite')
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)

      const statusStore = tx.objectStore(STORE_STATUSES)
      const metaStore = tx.objectStore(STORE_META)

      // Clear all existing statuses then write the new snapshot
      statusStore.clear()
      for (const [naptanId, status] of Object.entries(snapshot)) {
        statusStore.put(status, naptanId)
      }
      metaStore.put(etag, META_ETAG_KEY)
    })
    // Notify subscribers with the new snapshot (post-commit)
    this.notify(snapshot)
  }

  updates(subscriber: StationStatusCacheSubscriber): () => void {
    this.subscribers.add(subscriber)
    // Emit current snapshot immediately (warm-launch paint)
    void this.snapshot().then((snap) => subscriber(snap))
    return () => this.subscribers.delete(subscriber)
  }

  private notify(snapshot: StationStatusSnapshot): void {
    for (const sub of this.subscribers) {
      sub(snapshot)
    }
  }
}

// ---------------------------------------------------------------------------
// InMemoryStationStatusCache
// ---------------------------------------------------------------------------

/**
 * In-memory fallback cache (spec §7 "falls back to in-memory if IndexedDB is
 * unavailable"). Identical contract to IndexedDbStationStatusCache but
 * stores data in a plain JS Map. Used in SSR / test environments.
 *
 * Mirrors iOS `InMemoryStationStatusCache`.
 */
export class InMemoryStationStatusCache implements StationStatusCache {
  private data: StationStatusSnapshot = {}
  private currentEtag: string | undefined = undefined
  private readonly subscribers = new Set<StationStatusCacheSubscriber>()

  async status(naptanId: string): Promise<StationStatus | undefined> {
    return this.data[naptanId]
  }

  async snapshot(): Promise<StationStatusSnapshot> {
    return this.data
  }

  async etag(): Promise<string | undefined> {
    return this.currentEtag
  }

  async replace(snapshot: StationStatusSnapshot, etag: string): Promise<void> {
    this.data = snapshot
    this.currentEtag = etag
    this.notify(snapshot)
  }

  updates(subscriber: StationStatusCacheSubscriber): () => void {
    this.subscribers.add(subscriber)
    // Emit current snapshot immediately (warm-launch paint)
    subscriber(this.data)
    return () => this.subscribers.delete(subscriber)
  }

  private notify(snapshot: StationStatusSnapshot): void {
    for (const sub of this.subscribers) {
      sub(snapshot)
    }
  }
}
